import React from "react";
import { Box, Typography, alpha } from "@mui/material";
import {
  buildLegendEntries,
  ONLINE_LOCATION_COLOR,
} from "../../theme/locationColors";

/**
 * Location colours legend rendered above or below any calendar-style
 * surface. Reads the active locations list passed in (never hardcodes
 * Ajax / Scarborough / etc.) so a new location added in Settings shows up
 * here automatically. Pass `includeOnline` when the calendar can contain
 * online-only classes so the blue swatch is added.
 */
export interface LocationLegendProps {
  locations: Array<{ id: number; location_name?: string; location_color?: string | null }>;
  includeOnline?: boolean;
  title?: string;
  compact?: boolean;
  sx?: React.CSSProperties;
}

const LocationLegend: React.FC<LocationLegendProps> = ({
  locations,
  includeOnline,
  title = "Location Colours",
  compact,
}) => {
  const entries = buildLegendEntries(locations, { includeOnline });
  if (entries.length === 0) return null;

  const dotSize = compact ? 8 : 10;
  const gap = compact ? 1 : 1.5;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap,
        px: compact ? 1 : 1.5,
        py: compact ? 0.5 : 0.75,
        borderRadius: "10px",
        bgcolor: alpha("#64748b", 0.05),
        border: "1px solid rgba(15,23,42,0.06)",
      }}
    >
      {title && (
        <Typography
          sx={{
            fontSize: compact ? 10.5 : 11.5,
            fontWeight: 700,
            color: "text.disabled",
            textTransform: "uppercase",
            letterSpacing: 0.5,
            mr: 0.5,
          }}
        >
          {title}
        </Typography>
      )}
      {entries.map((e) => (
        <Box
          key={e.key}
          sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
          aria-label={`${e.label} colour`}
        >
          <Box
            sx={{
              width: dotSize,
              height: dotSize,
              borderRadius: "50%",
              bgcolor: e.color,
              boxShadow: `0 0 0 1px ${alpha("#000", 0.06)}`,
              flexShrink: 0,
            }}
          />
          <Typography sx={{ fontSize: compact ? 11.5 : 12.5, color: "text.secondary" }}>
            {e.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export { ONLINE_LOCATION_COLOR };
export default LocationLegend;
